import { useCallback, useEffect, useMemo, useState } from "react";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import _, { cloneDeep } from "lodash";
import moment from "moment";
import styled from "@emotion/styled";

import styledConst from "styles";
import { BlueText } from "components/common";
import ChartByTime, { TimeRangeConfig } from "components/dashboard/ChartByTime";
import { ChartAttribute } from "generated/models";
import { ConsumptionChartBy } from "types";
import {
    compareNumber,
    isHttpSuccess,
    formatNullableNumber,
} from "utils/functions";
import WidgetBox from "../WidgetBox";
import WidgetContent from "../WidgetContent";
import { meteringService } from "service/meteringService";
import { ALL_MONTHS_LIST } from "constant";

const DEFAULT_ACCURACY = "2";
const AverageText = {
    [ConsumptionChartBy.Day]: "Hourly",
    [ConsumptionChartBy.Week]: "Daily",
    [ConsumptionChartBy.Month]: "Daily",
    [ConsumptionChartBy.Year]: "Monthly",
};

type TProps = Readonly<
    Record<string, any> & {
        panel: Record<string, any> & {
            query_time: number | null;
            time_range: string;
            attributes: ChartAttribute[];
            uuid: string;
            name: string;
        };
    }
>;

export const fetchMeteringInfo = async ({
    year,
    month,
    organizationId,
}: {
    year: number;
    month?: number;
    organizationId: string;
}) => {
    if (!organizationId) {
        return null;
    }

    const currentYear = Number(year);

    const payload: {
        year: string;
        month?: string;
        in_token: boolean;
        previous_month?: boolean;
        previous_year?: boolean;
        organization_id?: string;
    } = {
        year: String(currentYear),
        in_token: true,
        organization_id: organizationId,
    };

    if (month === undefined) {
        payload.previous_year = true;
    } else {
        payload.month = String(month);
        payload.previous_month = true;
    }

    const meteringInfo: any = await meteringService.getMeteringInfo(payload);

    if (!isHttpSuccess(meteringInfo.status)) {
        return null;
    }

    const data = meteringInfo?.data?.[organizationId] ?? {};

    const currYearCounts = {
        email: 0,
        offload: 0,
        sms: 0,
        dashboard: 0,
        notification: 0,
    };
    const prevYearCounts = cloneDeep(currYearCounts);
    const currMonthlyCounts = [];
    const prevMonthlyCounts = [];

    let currMonthCounts = cloneDeep(currYearCounts);
    let prevMonthCounts = cloneDeep(prevYearCounts);
    if (month === undefined) {
        for (let month = 1; month <= 12; month++) {
            const monthData = data?.[month];
            currMonthlyCounts.push({
                monthIndex: month,
                data: monthData,
            });
            prevMonthlyCounts.push({
                monthIndex: month,
                data: data?.[-1]?.[month],
            });

            if (monthData) {
                currYearCounts.email += monthData.email ?? 0;
                currYearCounts.offload += monthData["data offload"] ?? 0;
                currYearCounts.sms += monthData.sms ?? 0;
                currYearCounts.dashboard += monthData.dashboard ?? 0;
                currYearCounts.notification += monthData.notification ?? 0;
            }

            const monthPrevYearData = data?.[-1]?.[month];
            if (monthPrevYearData) {
                prevYearCounts.email += monthPrevYearData.email ?? 0;
                prevYearCounts.offload +=
                    monthPrevYearData["data offload"] ?? 0;
                prevYearCounts.sms += monthPrevYearData.sms ?? 0;
                prevYearCounts.dashboard += monthPrevYearData.dashboard ?? 0;
                prevYearCounts.notification +=
                    monthPrevYearData.notification ?? 0;
            }
        }

    } else {
        const monthData = data?.[month];
        const prevMonthData = data?.[-1];

        currMonthCounts.email = monthData?.email ?? 0;
        currMonthCounts.offload = monthData?.["data offload"] ?? 0;
        currMonthCounts.sms = monthData?.sms ?? 0;
        currMonthCounts.dashboard = monthData?.dashboard ?? 0;
        currMonthCounts.notification = monthData?.notification ?? 0;

        prevMonthCounts.email = prevMonthData?.email ?? 0;
        prevMonthCounts.offload = prevMonthData?.["data offload"] ?? 0;
        prevMonthCounts.sms = prevMonthData?.sms ?? 0;
        prevMonthCounts.dashboard = prevMonthData?.dashboard ?? 0;
        prevMonthCounts.notification = prevMonthData?.notification ?? 0;
    }

    const currYearTotal = Object.values(currYearCounts).reduce(
        (sum, val) => sum + val,
        0
    );

    const prevYearTotal = Object.values(prevYearCounts).reduce(
        (sum, val) => sum + val,
        0
    );

    return {
        yearData: {
            currYearCounts,
            currYearTotal,
            prevYearTotal,
            currMonthlyCounts,
            prevMonthlyCounts,
        },
        monthData: {
            currMonthCounts,
            prevMonthCounts,
        },
    };
};

const WidgetFooter = styled.div`
    position: absolute;
    bottom: 0;
    right: 0;
    white-space: nowrap;
    z-index: 1;
`;

const PercentageBreakdownWrapper = styled.div`
    position: absolute;
    top: 3px;
    left: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 14px;

    .total {
        font-size: 24px;
        color: ${styledConst.Primary_Green_1};
        font-weight: 500;
    }

    .muted {
        color: #828fab;
    }

    .increase-icon-wrapper {
        color: ${styledConst.Secondary_Red};
    }

    .decrease-icon-wrapper {
        color: ${styledConst.Primary_Green_1};
    }

    .diff-trend-wrapper {
        display: flex;
        gap: 5px;
        height: 16px;
    }

    .diff-trend-value {
        color: #fff;
    }
`;

const StyledWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const FilterWrapper = styled.div`
    position: absolute;
    right: 0;
    top: 12px;
    z-index: 1;
`;

const PercentageBreakdown = ({
    previous,
    current,
}: {
    previous: number | null;
    current: number | null;
}) => {
    const percent = compareNumber(current, previous);

    return (
        <PercentageBreakdownWrapper>
            <div className="muted">Token Consumption</div>
            <div style={{ display: "flex", gap: 5 }}>
                <div className="total">{current ?? "-"}</div>
                <div className="muted">(Total for the year)</div>
            </div>
            <div className="diff-trend-wrapper">
                {percent && (
                    <>
                        <div className="diff-trend-value">
                            {percent.direction === "decrease" ? (
                                <span className="decrease-icon-wrapper">↓</span>
                            ) : (
                                <span className="increase-icon-wrapper">↑</span>
                            )}{" "}
                            {percent.percentageToShow}%
                        </div>
                        <div className="muted">
                            {percent.direction === "decrease"
                                ? "decrease"
                                : "increase"}{" "}
                            from previous year
                        </div>
                    </>
                )}
            </div>
        </PercentageBreakdownWrapper>
    );
};

const TokenStackedChart = (props: TProps) => {
    const { panel, isVisible, visibleRef } = props;
    const { name = "", attributes } = panel;
    const organizationId = _.get(
        attributes,
        "[0].organization_id",
        ""
    );

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tokenData, setTokenData] = useState<{
        currYearTotal: number;
        prevYearTotal: number;
        monthlyCounts: any[];
        prevYearMonthlyCounts: any[];
        average: number;
    } | null>(null);
    const [selectTime, setSelectTime] = useState<TimeRangeConfig["lastTime"]>(
        moment()
    );

    const fetchNewData = useCallback(async () => {
        const currentYear = selectTime.year();

        try {
            const meteringInfo = await fetchMeteringInfo({
                year: currentYear,
                organizationId,
            });
            const yearData = meteringInfo?.yearData;
            if (!yearData) {
                return;
            }

            const now = moment();
            let elapsedMonths = 12;
            if (currentYear === now.year()) {
                elapsedMonths = now.month() + 1;
            } else if (currentYear > now.year()) {
                elapsedMonths = 0;
            }

            const average =
                elapsedMonths === 0
                    ? 0
                    : yearData.currYearTotal / elapsedMonths;

            setTokenData({
                monthlyCounts: yearData.currMonthlyCounts,
                prevYearMonthlyCounts: yearData.prevMonthlyCounts,
                currYearTotal: yearData.currYearTotal,
                prevYearTotal: yearData.prevYearTotal,
                average,
            });
        } finally {
            setIsLoading(false);
        }
    }, [selectTime, organizationId]);

    useEffect(() => {
        if (!isVisible) {
            return;
        }

        fetchNewData();
    }, [isVisible, fetchNewData]);

    const onTimeChange = async (timeRange: TimeRangeConfig) => {
        setSelectTime(timeRange.lastTime);
    };

    const option: EChartsOption = useMemo(
        () => ({
            backgroundColor: "#2f3a51",
            color: ["#EE9EC6", "#F6C39D", "#FFEFB5", "#A2DFD8", "#52BFD4"],
            responsive: true,
            maintainAspectRatio: false,
            tooltip: {
                trigger: "item",
            },
            legend: {
                selectedMode: true,
                bottom: 0,
                right: 200,
                textStyle: {
                    color: "#ffffff",
                    fontFamily: "Roboto",
                    fontSize: 14,
                    fontWeight: 400,
                },
                padding: [
                    5, // up
                    10, // right
                    0, // down
                    0, // left
                ],
                itemWidth: 6,
                itemHeight: 6,
                icon: "circle",
            },
            series: tokenData
                ? [
                    {
                        name: "Email",
                        type: "bar",
                        stack: "total",
                        barWidth: 10,
                        emphasis: {
                            focus: "series",
                        },
                        data: tokenData.monthlyCounts.map(
                            (month) => month.data?.email ?? 0
                        ),
                    },
                    {
                        name: "SMS",
                        type: "bar",
                        stack: "total",
                        barWidth: 10,
                        emphasis: {
                            focus: "series",
                        },
                        data: tokenData.monthlyCounts.map(
                            (month) => month.data?.sms ?? 0
                        ),
                    },
                    {
                        name: "Notification",
                        type: "bar",
                        stack: "total",
                        barWidth: 10,
                        emphasis: {
                            focus: "series",
                        },
                        data: tokenData.monthlyCounts.map(
                            (month) => month.data?.notification ?? 0
                        ),
                    },
                    {
                        name: "Dashboard Data",
                        type: "bar",
                        stack: "total",
                        barWidth: 10,
                        emphasis: {
                            focus: "series",
                        },
                        data: tokenData.monthlyCounts.map(
                            (month) => month.data?.dashboard ?? 0
                        ),
                    },
                    {
                        name: "Data Download",
                        type: "bar",
                        stack: "total",
                        barWidth: 10,
                        emphasis: {
                            focus: "series",
                        },
                        data: tokenData.monthlyCounts.map(
                            (month) => month.data?.["data offload"] ?? 0
                        ),
                    },
                ]
                : [],
            xAxis: {
                data: ALL_MONTHS_LIST,
                boundaryGap: true,
                axisLine: {
                    lineStyle: {
                        color: "#48597D",
                    },
                },
            },
            yAxis: {
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: "#4B5569",
                    },
                },
            },
            grid: {
                bottom: 58,
                left: 10,
                right: 10,
                top: 105,
                containLabel: true,
            },
        }),
        [tokenData]
    );

    return (
        <WidgetBox ref={visibleRef} className="widget-box w-100">
            <WidgetContent title={name || "Token Spending"} loading={isLoading}>
                <StyledWrapper>
                    <FilterWrapper>
                        <ChartByTime
                            timeRange={{
                                lastTime: selectTime,
                                timeRange: "", // Pass empty string to by pass ts validation, not used in this chart
                            }}
                            onChange={(timeRange) => onTimeChange(timeRange)}
                            view={ConsumptionChartBy.Year}
                            validViews={[ConsumptionChartBy.Year]}
                        />
                    </FilterWrapper>

                    <PercentageBreakdown
                        current={tokenData?.currYearTotal ?? null}
                        previous={tokenData?.prevYearTotal ?? null}
                    />

                    <WidgetFooter>
                        <BlueText>
                            {AverageText[ConsumptionChartBy.Year]} Avg:{" "}
                            <span className="text-white">
                                {formatNullableNumber(
                                    tokenData?.average ?? null,
                                    DEFAULT_ACCURACY
                                )}
                            </span>
                        </BlueText>
                    </WidgetFooter>

                    <div style={{ height: "100%", width: "100%" }}>
                        <ReactECharts
                            style={{ height: "100%", width: "100%" }}
                            theme={"dark"}
                            option={option}
                        />
                    </div>
                </StyledWrapper>
            </WidgetContent>
        </WidgetBox>
    );
};

export default TokenStackedChart;
